import { Injectable, Injector } from '@angular/core';
import { delay, map, Observable, of, tap } from 'rxjs';
import { AppComponentBase } from '@shared/common/app-component-base';
import {
    ApplicationStatus,
    CreateNdqrApplicationDto,
    CreateOrUpdateNdqrRegisterOutputDto,
    ExternalSystemEnum,
    NdqrBusinessDto,
    NdqrBusinessServiceProxy,
    NdqrDto, NdqrRegisterDto,
    NdqrRegisterServiceProxy,
    NdqrServiceProxy,
    NdqrStatus,
    PersonApplicationSummaryDto
} from '@shared/service-proxies/service-proxies';
import { ApplicationService } from '../application/application.service';

@Injectable()
export class NdqrService extends AppComponentBase {

    ndqr: NdqrDto;

    constructor(
        injector: Injector,
        private readonly applicationService: ApplicationService,
        private readonly ndqrService: NdqrServiceProxy,
        private readonly ndqrRegisterService: NdqrRegisterServiceProxy,
        private readonly ndqrBusinessService: NdqrBusinessServiceProxy
    ) {
        super(injector);
    }

    createNdqr(professionId: string, legitimationId: number): Observable<string> {
        this.closeNdqr();
        return this.ndqrService.createNdqr(new CreateNdqrApplicationDto({
            professionId: professionId,
            legitimationId: legitimationId
        })).pipe();
    }

    getNdqr(caseId: string): Observable<NdqrDto> {
        this.closeNdqr();
        return this.ndqrService.getNdqr(caseId).pipe(
            tap((output) => {
                this.ndqr = output;
            })
        );
    }

    closeNdqr(): void {
        this.ndqr = null;
    }

    getMyNdqrs(): Observable<PersonApplicationSummaryDto[]> {
        return this.ndqrService.getMyNdqrs();
    }

    getNdqrRegister(): Observable<NdqrRegisterDto> {
        return of(this.ndqr.register).pipe(delay(1));
    }

    getLegitimatedGlnForRegister(): Observable<string> {
        return this.ndqrRegisterService.getLegitimatedGlnForRegister(this.caseId);
    }

    createOrUpdateNdqrRegister(): Observable<CreateOrUpdateNdqrRegisterOutputDto> {
        this.applicationService.setStepToReview(NdqrStatus.Register);
        return this.ndqrRegisterService.createOrUpdateEhcRegister(this.caseId).pipe(
            tap((output: CreateOrUpdateNdqrRegisterOutputDto) => {
                this.ndqr.register = output.register;
            })
        );
    }

    acceptRegisterCheck(): Observable<NdqrStatus> {
        this.applicationService.setStepToReview(NdqrStatus.Register);
        return this.ndqrRegisterService.acceptRegisterCheck(this.caseId).pipe(
            tap((status: NdqrStatus) => {
                this.ndqr.currentStep = status;
            })
        );
    }

    getTotalWorkedDaysForYear(year: number): Observable<number> {
        return this.ndqrBusinessService.getNumberOfTotalWorkedDays(this.caseId, year);
    }

    getBusiness(): Observable<NdqrBusinessDto> {
        return of(this.ndqr.business).pipe(delay(1));
    }

    createOrUpdateBusiness(dto: NdqrBusinessDto): Observable<NdqrStatus> {
        return this.ndqrBusinessService.createOrUpdateBusiness(this.caseId, dto).pipe(tap(x => {
            this.ndqr.business = x.business;
            this.ndqr.currentStep = x.status;
        }), map(x => x.status));
    }

    getEvaluationComments(): Observable<string> {
        return this.ndqrService.getEvaluationComments(this.caseId);
    }

    releaseNdqr(comment: string): Observable<void> {
        return this.ndqrService.releaseNdrq(this.caseId, comment);
    }

    setNdqrRegisterStepToReview() {
        this.applicationService.setStepToReview(NdqrStatus.Register);
    }

    getNextStatus(status: NdqrStatus): Observable<NdqrStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.ndqrService.getNextStatus(this.caseId, status).pipe(map(status => {
            // Authority goes to 'finish review' and not to 'release'
            if (isAuthority && status === NdqrStatus.Release) {
                return NdqrStatus.FinishReview;
            }
            return status;
        }));
    }

    getStepUrl(nextStep: NdqrStatus): string {
        return this.applicationService.getNextStep(NdqrService.getUrlForStep(nextStep));
    }

    static getUrlForStep(step: NdqrStatus): string {
        switch (step) {
            case NdqrStatus.FinishReview:
                return 'finish-review';
            case NdqrStatus.Register:
                return 'register';
            case NdqrStatus.Business:
                return 'business';
            case NdqrStatus.Release:
                return 'release';
            case NdqrStatus.Released:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }

    isInStatus(expectedStatus: NdqrStatus): boolean {
        return this.ndqr.currentStep === expectedStatus;
    }

    getExternalSystemByProfessionId(): Observable<ExternalSystemEnum> {
        return this.ndqrService.getExternalRegisterByProfessionId(this.profession);
    }

    get caseId(): string {
        return this.ndqr?.application?.caseId;
    }

    get status(): ApplicationStatus {
        return this.ndqr.application.status;
    }

    get currentStep(): NdqrStatus {
        return this.ndqr.currentStep;
    }

    get profession(): string {
        return this.ndqr?.professionId;
    }

    get medRegId(): number {
        return this.ndqr.register.medRegId;
    }

    get hasMedRegId(): boolean {
        return this.ndqr.register?.medRegId != null;
    }

    get psyRegId(): number {
        return this.ndqr.register.psyRegId;
    }

    get hasPsyRegId(): boolean {
        return this.ndqr.register?.psyRegId != null;
    }

    get naregId(): number {
        return this.ndqr.register.naregId;
    }

    get hasNaregId(): boolean {
        return this.ndqr.register?.naregId != null;
    }
}

